import { type Dispatch, type SetStateAction, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { GbuLoeschErgebnis, NeueZeileOptionen } from '../../../services/sifa/gbuService';
import { type AutoSpeichern, useAutoSpeichern } from './useAutoSpeichern';
import { antwortStattEntwurf, istAelter, istVeraltet, neuesterStand, zeilenFolge } from './gbuModulLogic';

/** Die reinen Regeln einer Zeilenart (gbuModulLogic) — je Art EIN festes Objekt. */
export interface ZeilenRegeln<Z, E> {
  entwurfAus: (z: Z) => E;
  /** Weicht der Entwurf (bereinigt) von der Zeile ab? */
  geaendert: (z: Z, e: E) => boolean;
  /** Neue Zeile: Eingaben gegenueber dem Ausgangsstand (auch unvollstaendige)? */
  begonnen: (start: E, e: E) => boolean;
  /** Neue Zeile: genug Inhalt zum Anlegen? */
  sinnvoll: (e: E) => boolean;
  bereinige: (e: E) => E;
  pruefe: (e: E) => string[];
}

export type VerwerfErgebnis<Z> =
  | { art: 'verworfen' }
  /** Ob das Anlegen ankam, ist unklar (Antwort verloren, Loeschen scheiterte) — frisch laden zeigt es. */
  | { art: 'unklar' }
  /** Angelegt, Loeschen scheiterte: die Zeile bleibt als gespeicherte stehen. */
  | { art: 'steht'; zeile: Z; fehler: unknown };

interface Optionen<Z, E> {
  /** Gespeicherte Zeile (Prop) bzw. null fuer eine neue. */
  zeile: Z | null;
  vorlage: E | undefined;
  regeln: ZeilenRegeln<Z, E>;
  bearbeitbar: boolean;
  /** Schreibt (ohne `id`: legt an, mit `opts.neueId`). */
  schreibe: (entwurf: E, id: string | undefined, opts: NeueZeileOptionen | undefined) => Promise<Z>;
  loesche: (id: string) => Promise<GbuLoeschErgebnis>;
  onGespeichert: (z: Z) => void;
  /** Ein fremder neuerer Stand wurde in den Entwurf uebernommen (z. B. Messwert-Text nachziehen). */
  onUebernommen?: (z: Z) => void;
  /** Zusaetzliche Eingaben ausserhalb des Entwurfs (z. B. Messwert-Text): Fingerabdruck und Gruende. */
  zusatzSchluessel?: string;
  zusatzGruende?: string[];
}

export interface ZeilenEntwurf<Z, E> {
  e: E;
  setE: Dispatch<SetStateAction<E>>;
  auto: AutoSpeichern;
  gruende: string[];
  verworfen: boolean;
  /** Neue Zeile verwerfen: laufendes Anlegen abwarten und eine angelegte Zeile wieder loeschen. */
  verwerfen: () => Promise<VerwerfErgebnis<Z>>;
  /** Gespeicherte Zeile loeschen bzw. entfallen lassen — ein laufender Lauf wird abgewartet, danach keiner mehr. Wirft. */
  loeschen: (z: Z) => Promise<GbuLoeschErgebnis>;
}

/**
 * Entwurf und automatisches Speichern EINER Kind-Zeile (Gefaehrdung,
 * Massnahme, Vorsorge):
 *
 *  - Lost Update: die eigene Antwort wird nie wie ein fremder Stand
 *    eingespielt (zeilenFolge). Nach der eigenen Antwort gilt sie nur, wenn
 *    seit Laufbeginn nichts getippt wurde UND sie nach Bereinigung abweicht
 *    (antwortStattEntwurf) — sonst bleibt der Entwurf wie getippt.
 *  - Neue Zeilen: EINE Kennung je Zeile (crypto.randomUUID) bei jedem
 *    Anlegeversuch — ein Neuversuch nach verlorener Antwort wird zum Update.
 *    Ungespeichert gilt sie schon mit unvollstaendigen Eingaben (Faktor, W/S
 *    …), angelegt wird sie erst mit Inhalt.
 *  - Verwerfen sperrt weitere Laeufe; eine spaete Antwort wird geloescht statt
 *    uebernommen — eine verworfene Zeile landet nie in der Fassung.
 */
export const useZeilenEntwurf = <Z extends { id: string; updatedAt?: string | null }, E>({
  zeile, vorlage, regeln, bearbeitbar, schreibe, loesche, onGespeichert, onUebernommen, zusatzSchluessel = '', zusatzGruende = [],
}: Optionen<Z, E>): ZeilenEntwurf<Z, E> => {
  const [start] = useState<E>(() => (zeile ? regeln.entwurfAus(zeile) : (vorlage as E)));
  const [e, setE] = useState<E>(start);
  const [neueId] = useState(() => crypto.randomUUID());
  /** Zuletzt SELBST gespeicherte Zeile: kommt sie als Prop zurueck, ist das kein fremder Stand. */
  const [eigen, setEigen] = useState<Z | null>(null);
  const [verworfen, setVerworfen] = useState(false);
  const verworfenRef = useRef(false);
  /** Ein Anlegen wurde abgeschickt (Antwort evtl. verloren). */
  const versuchtRef = useRef(false);

  // Neuer Stand von aussen (Prop): nur ein fremder, NEUERER Stand ersetzt einen unveraenderten
  // Entwurf; ein veralteter (spaete Antwort eines Neuladens) wird ganz ignoriert (zeilenFolge).
  const [basis, setBasis] = useState<Z | null>(zeile);
  if (zeile !== basis) {
    const folge = zeilenFolge({ neu: zeile, basis, eigen, entwurfGeaendert: (gegen) => regeln.geaendert(gegen, e) });
    // Gleich nach Bereinigung: der Entwurf bleibt wie getippt (kein Springen im Feld).
    if (folge === 'uebernehmen' && zeile && regeln.geaendert(zeile, e)) {
      setE(regeln.entwurfAus(zeile));
      onUebernommen?.(zeile);
    }
    if (folge === 'uebernehmen' || folge === 'nurBasis') setBasis(zeile);
  }
  /** Der neueste bekannte Stand (Basis bzw. eigene Antwort) — gegen ihn wird verglichen und gespeichert. */
  const stand = neuesterStand(basis, eigen);

  /** Zuletzt gespeicherte Zeile — nach Erfolg SOFORT gesetzt, damit ein nachgezogener Lauf aktualisiert statt neu anzulegen. */
  const zeileRef = useRef<Z | null>(zeile);
  // Nur vorwaerts: nie ein aelterer Stand als der schon bekannte.
  useLayoutEffect(() => {
    const z = zeileRef.current;
    if (stand && (!z || z.id !== stand.id || !istAelter(stand.updatedAt, z.updatedAt))) zeileRef.current = stand;
  }, [stand]);

  // Haelt der Editor noch den veralteten Stand (spaetes Neuladen), bekommt er den neuesten zurueck —
  // sonst zeigten Vorschau und Ausgabe den alten Text.
  useEffect(() => {
    if (zeile && stand && istVeraltet(zeile, stand, eigen)) onGespeichert(stand);
  }, [zeile, stand, eigen, onGespeichert]);

  const gruende = [...regeln.pruefe(e), ...zusatzGruende];
  const geaendert = !verworfen && (stand ? regeln.geaendert(stand, e) : regeln.begonnen(start, e));
  const auto = useAutoSpeichern({
    geaendert: bearbeitbar && geaendert,
    bereit: bearbeitbar && !verworfen && gruende.length === 0,
    schluessel: `${JSON.stringify(e)}|${zusatzSchluessel}`,
    // Gegen den zuletzt BESTAETIGTEN Stand (zeileRef, nach jeder Antwort sofort gesetzt).
    zuTun: () => {
      if (verworfenRef.current) return false;
      const zuletzt = zeileRef.current;
      return zuletzt ? regeln.geaendert(zuletzt, e) : regeln.sinnvoll(e);
    },
    speichere: async () => {
      if (verworfenRef.current) return;
      const zuletzt = zeileRef.current;
      const gesendet = e;
      if (zuletzt ? !regeln.geaendert(zuletzt, gesendet) : !regeln.sinnvoll(gesendet)) return;
      if (!zuletzt) versuchtRef.current = true;
      const z = await schreibe(regeln.bereinige(gesendet), zuletzt?.id, zuletzt ? undefined : { neueId });
      zeileRef.current = z;
      // Waehrend des Laufs verworfen: nicht uebernehmen — verwerfen() raeumt die Zeile weg.
      if (verworfenRef.current) return;
      const weichtAb = regeln.geaendert(z, gesendet);
      setE((p) => (antwortStattEntwurf(p, gesendet, weichtAb) ? regeln.entwurfAus(z) : p));
      setEigen(z);
      onGespeichert(z);
    },
  });

  const verwerfen = async (): Promise<VerwerfErgebnis<Z>> => {
    verworfenRef.current = true;
    setVerworfen(true);
    await auto.warte();
    const z = zeileRef.current;
    if (!z && !versuchtRef.current) return { art: 'verworfen' };
    try {
      await loesche(z?.id ?? neueId);
      return { art: 'verworfen' };
    } catch (fehler) {
      if (!z) return { art: 'unklar' };
      // Angelegt, aber nicht weggeraeumt: als gespeicherte Zeile stehen lassen, damit sie sichtbar bleibt.
      verworfenRef.current = false;
      setVerworfen(false);
      setEigen(z);
      onGespeichert(z);
      return { art: 'steht', zeile: z, fehler };
    }
  };

  const loeschen = async (z: Z): Promise<GbuLoeschErgebnis> => {
    verworfenRef.current = true;
    try {
      await auto.warte();
      return await loesche(z.id);
    } catch (err) {
      verworfenRef.current = false;
      throw err;
    }
  };

  return { e, setE, auto, gruende, verworfen, verwerfen, loeschen };
};
